import { inject, Injectable } from "@angular/core";
import { Address } from "../../contract/Address/Address";
import { MixedRealEstateService } from "../../../modules/real-estate/inherit-real-estate-service/mixed-real-estate.service";
import { InterfaceStatus } from "../../contract/Enum/Status";
import { catchError, map, Observable, of } from "rxjs";
import { CommercialAVMRequest } from "../../contract/CommercialRealEstate/CommercialAVMRequest";
import { ObjectInformation } from "../../contract/ObjectInformation/ObjectInformation";
import { RealEstateBaseObject } from "../../../modules/real-estate/models/real-estate-base-object.interface";
import { LoadingService } from "../../../modules/loading/loading.service";
import { ObjectInformationService } from "../../../modules/object-information/object-information.service";
import { RealEstateStoreService } from "../../../modules/real-estate/services/real-estate-store.service";
import { NotificationService } from "@services/notification.service";
import { TranslocoService } from "@jsverse/transloco";
import { RealEstateService } from "@services/real-estate/real-estate.service";
import { DataModeService } from "../../../modules/menu/data-mode.service";
import { CapitalizedIncomeValueService } from "../../../modules/capitalized-income-value/capitalized-income-value.service";
import { AccumateModule } from "../../consts/icon-features";
import { EstateType } from "../../contract/Enum/portfolio.enum";
import { ThematicInfoService } from "./thematic-info/thematic-info.service";

@Injectable({
  providedIn: "root"
})
export class RealEstateInitService {
  private readonly objectInformationService = inject(ObjectInformationService);
  private readonly loadingService = inject(LoadingService);
  private readonly notificationService = inject(NotificationService);
  private readonly translocoService = inject(TranslocoService);
  private readonly dataModeService = inject(DataModeService);
  private readonly thematicInfoService = inject(ThematicInfoService);

  private readonly realEstateStoreService = inject(RealEstateStoreService);
  private readonly oldRealEstateService = inject(RealEstateService);
  private readonly capitalizedIncomeValueService = inject(CapitalizedIncomeValueService);
  private readonly mixedRealEstateService = inject(MixedRealEstateService);

  initializeNewRealEstateObject(
    accumateModule: AccumateModule,
    estateType: EstateType,
    commercialRealEstateRequest: CommercialAVMRequest,
    address: Address,
    objectInformation?: ObjectInformation
  ): Observable<boolean> {
    this.objectInformationService.createNewObject = true;
    this.loadingService.hide();

    const requestAddress: Address = {
      coordinate: address.coordinate,
      city: address.city,
      street: address.street,
      houseNumber: address.houseNumber,
      zip: address.zip
    };

    return this.newRealEstateObject(accumateModule, estateType, requestAddress, objectInformation, commercialRealEstateRequest.customerId);
  }

  newRealEstateObject(
    accumateModule: AccumateModule,
    estateType: EstateType,
    address?: Address,
    objectInformation?: ObjectInformation,
    customerId?: string
  ): Observable<boolean> {
    if (estateType === EstateType.Mixed) {
      return this.mixedRealEstateService.createAndSaveFromDialog(address, null);
    }

    return this.oldRealEstateService.createNewRealEstateObject(true, estateType, address, objectInformation, customerId).pipe(
      map(newRealEstateObject => {
        if (!newRealEstateObject) {
          return false;
        }

        if (newRealEstateObject?.status !== InterfaceStatus.OK) {
          this.handleInterfaceError(newRealEstateObject);
          return false;
        }

        this.thematicInfoService.isRent.set(true);
        this.realEstateStoreService.setRealEstate(newRealEstateObject, accumateModule);

        // ToDo: @Phillip - wie ist die Idee? Müssen wir uns hier noch um ein eventuelles Reset kümmern oder soll das an eine andere Stelle?
        if (estateType === EstateType.Commercial) {
          this.capitalizedIncomeValueService.createInternalCapitalizedIncomeValueData();
          this.capitalizedIncomeValueService.setCapitalizedIncomeValue();
        }

        return true;
      }),
      catchError(err => {
        console.error(err);
        return of(false);
      })
    );
  }

  handleInterfaceError(realEstateResponse: RealEstateBaseObject) {
    if (realEstateResponse.status === InterfaceStatus.ActivePackagePromoNotInCountry) {
      this.notificationService.failure("Exception.Failure.Status." + realEstateResponse.status, {
        Country: this.translocoService.translate("Properties.Country." + realEstateResponse.address.coordinate.country)
      });
    }

    this.notificationService.defaultError();
    this.dataModeService.resetSelectedDataMode();
  }
}
